import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import isPostalCode from 'validator/lib/isPostalCode';
import includes from 'lodash/includes';
import uniqueId from 'lodash/uniqueId';
import without from 'lodash/without';
import pick from 'lodash/pick';
import keyboardKey from 'keyboard-key';
import Button from '../Button';
import Dimmer from '../Dimmer';
import Loader from '../Loader';
import Dropdown from '../Dropdown';
import Form from '../Form';
import Checkbox from '../Checkbox';
import messages from './messages';
import Label from '../Label';
import CollegeEventFilterMatches from '../../containers/CollegeEventFilterMatches';
import CollegeEventsFilterFormCancelMessage from './cancel-message';
import { genderOptions, ethnicityOptions, gradeLevelOptions, letterGradeOptions } from '../CollegeEventsFilterPreview/messages';
import './styles.css';

const distanceOptions = [25, 50, 100, 200, 250];

export class CollegeEventsFilterFormComponent extends React.PureComponent {
  static propTypes = {
    intl: PropTypes.shape({
      formatMessage: PropTypes.func.isRequired,
    }).isRequired,
    onSave: PropTypes.func.isRequired,
    saving: PropTypes.bool.isRequired,
    saveError: PropTypes.shape({
      id: PropTypes.string.isRequired,
    }),
    onCancel: PropTypes.func.isRequired,
    filter: PropTypes.shape({
      genders: PropTypes.arrayOf(PropTypes.string),
      distanceUnit: PropTypes.string,
      distanceValue: PropTypes.number,
      postalCode: PropTypes.string,
      ethnicities: PropTypes.arrayOf(PropTypes.string),
      gradeLevels: PropTypes.arrayOf(PropTypes.string),
      gpaScores: PropTypes.arrayOf(PropTypes.string),
      name: PropTypes.string,
      visible: PropTypes.bool,
    }).isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      ...props.filter,
      distanceUnit: 'MILES',
      validationError: false,
      name: '',
      missingPostalCode: false,
      missingLocation: false,
      missingLocationandPostalCode: false,
      missingFilterName: false,
      badPostalCode: false,
      isCancelling: false,
      nameAlreadyExist: false,
      incorrectFiveDigitsPostalCode: false,
      serviceUnavailable: false,
    };
    this.ethnicityDropdownRef = React.createRef();
    this.gradeLevelDropdownRef = React.createRef();
    this.gpaScoreDropdownRef = React.createRef();
    this.labelAlert = React.createRef();
  }

  componentWillReceiveProps(nextProps) {
    if (this.props.filter !== nextProps.filter) {
      const updatedForm = { ...nextProps.filter, distanceUnit: 'MILES', validationError: false };
      this.setState(updatedForm);
    }
    if (nextProps.saveError && this.props.saveError !== nextProps.saveError) {
      this.setErrors(nextProps.saveError);
    }
  }

  onDropdownKeydown = (key, id) => {
    if (key !== keyboardKey.Backspace && key !== keyboardKey.Delete) return;
    this.setState((state) => ({ [id]: state[`${id}`].slice(0, state[`${id}`].length - 1) }));
  };

  onChange = (e, { name, value } = {}) => {
    name = name || e.target.name;
    value = value !== undefined ? value : e.target.value;

    if (name === 'genders') {
      // eslint-disable-next-line react/no-access-state-in-setstate
      const newArray = includes(this.state[name], value) ? without(this.state[name], value) : this.state[name].concat(value);
      this.setState({ [name]: newArray });
    } else {
      this.setState({ [name]: value });
    }

    this.setState({
      badPostalCode: false,
      missingPostalCode: false,
      incorrectFiveDigitsPostalCode: false,
      missingLocationandPostalCode: false,
    });
  };

  onSubmit = (e) => {
    e.preventDefault();

    const { distanceValue, postalCode, name } = this.state;

    let missingPostalCode = false;
    let missingLocation = false;
    let missingLocationandPostalCode = false;
    let missingFilterName = false;
    let badPostalCode = false;
    const validationError = !distanceValue || !postalCode || !name.trim() || (postalCode && !isPostalCode(postalCode, 'US'));

    if (!distanceValue && postalCode) {
      missingLocation = (
        <Label key={uniqueId('missing-loc-label-')} basic color="red" pointing="above" styleName="missing-loc">
          <FormattedMessage {...messages.missingLocation} />
        </Label>
      );
      if (this.labelAlert.current != null) {
        this.labelAlert.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }

    if (distanceValue && (!postalCode || postalCode.trim() === '')) {
      missingPostalCode = (
        <Label key={uniqueId('missing-post-label-')} basic color="red" pointing="above" styleName="missing-post-label">
          <FormattedMessage {...messages.missingPostalCode} />
        </Label>
      );
      if (this.labelAlert.current != null) {
        this.labelAlert.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }

    if (!distanceValue && !postalCode) {
      missingLocationandPostalCode = (
        <Label key={uniqueId('missing-loc-post-label-')} basic color="red" pointing="above" styleName="missing-loc-post">
          <FormattedMessage {...messages.missingLocationandPostalCode} />
        </Label>
      );
      if (this.labelAlert.current != null) {
        this.labelAlert.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }

    if (!name || name.trim() === '') {
      missingFilterName = (
        <Label key={uniqueId('missing-filter-label-')} basic color="red" pointing="left">
          <FormattedMessage {...messages.missingFilterName} />
        </Label>
      );
    }

    if (postalCode && !isPostalCode(postalCode, 'US')) {
      badPostalCode = (
        <Label key={uniqueId('bad-post-label-')} basic color="red" pointing="above" styleName="bad-post-label">
          <FormattedMessage {...messages.badPostalCode} />
        </Label>
      );
      if (this.labelAlert.current != null) {
        this.labelAlert.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }

    this.setState({
      missingLocation,
      missingFilterName,
      missingPostalCode,
      missingLocationandPostalCode,
      badPostalCode,
      validationError,
    });

    if (missingLocation || missingFilterName || missingPostalCode || missingLocationandPostalCode || badPostalCode) {
      return;
    }
    this.props.onSave(pick(this.state, ['distanceUnit', 'distanceValue', 'ethnicities',
      'genders', 'gradeLevels', 'gpaScores', 'name', 'postalCode', 'visible']));
  };

  handleChange = (evt) => {
    if (!evt.target.validity.valid) return;
    this.setState({ name: evt.target.value });
  };

  setErrors(saveError) {
    if (saveError.defaultMessage.includes(messages.errorUniqueName.defaultMessage)) {
      this.setState({
        nameAlreadyExist: true,
        incorrectFiveDigitsPostalCode: false,
      });
    } else if (saveError.defaultMessage.includes(messages.incorrectPostalCode.defaultMessage)) {
      this.setState({
        incorrectFiveDigitsPostalCode: true,
        nameAlreadyExist: false,
      });
    } else if (saveError.defaultMessage.includes(messages.serviceUnavailable.defaultMessage)) {
      this.setState({
        serviceUnavailable: true,
      });
    }
  }

  handleFilterErrors = () => {
    this.setState({
      nameAlreadyExist: false,
      incorrectFiveDigitsPostalCode: false,
    });
  };

  renderButtons() {
    const { saving } = this.props;
    return (
      <React.Fragment>
        <Button
          data-cy="create-filter-form-save-button"
          styleName="save-button"
          floated="right"
          type="submit"
          loading={saving}
          primary
        >
          <FormattedMessage {...messages.save} />
        </Button>
        <Button
          data-cy="create-filter-form-cancel-button"
          styleName="cancel-button"
          floated="left"
          type="button"
          loading={saving}
          onClick={() => this.setState({ isCancelling: true })}
        >
          <FormattedMessage {...messages.cancel} />
        </Button>
      </React.Fragment>
    );
  }

  renderCancelMessage() {
    if (!this.state.isCancelling) return null;
    return (
      <CollegeEventsFilterFormCancelMessage
        onContinue={() => this.setState({ isCancelling: false })}
        onCancel={this.props.onCancel}
      />
    );
  }

  render() {
    const { intl, saving, saveError } = this.props;
    const {
      genders,
      name,
      distanceValue,
      postalCode,
      ethnicities,
      gradeLevels,
      gpaScores,
      validationError,
      badPostalCode,
      missingLocation,
      missingPostalCode,
      missingLocationandPostalCode,
      missingFilterName,
      nameAlreadyExist,
      incorrectFiveDigitsPostalCode,
      serviceUnavailable,
    } = this.state;
    const filter = {
      postalCode,
      distanceValue,
      distanceUnit: 'MILES',
      gradeLevels,
      ethnicities,
      gpaScores,
      genders,
    };
    const params = { filter };

    return (
      <Dimmer.Dimmable>
        <Dimmer
          role="progressbar"
          aria-busy={saving}
          inverted
          active={saving}
        >
          <Loader active={saving} />
        </Dimmer>
        <Form data-cy="create-filter-form" onSubmit={this.onSubmit} size="small" error={!!saveError} styleName="form">
          <div styleName="row">
            <div id="Gender" styleName="label"><FormattedMessage {...messages.gender} /></div>
            <div role="group" aria-labelledby="Gender" styleName="fields border">
              <span styleName="checkbox">
                <Checkbox
                  data-cy="create-filter-form-gender-female-checkbox"
                  id="genders_female"
                  name="genders"
                  onChange={this.onChange}
                  value="FEMALE"
                  checked={includes(genders, 'FEMALE')}
                  label={{
                    htmlFor: 'genders_female',
                    children: (<FormattedMessage {...genderOptions.FEMALE} />),
                  }}
                />
              </span>
              <span styleName="checkbox">
                <Checkbox
                  data-cy="create-filter-form-gender-male-checkbox"
                  id="genders_male"
                  name="genders"
                  onChange={this.onChange}
                  value="MALE"
                  checked={includes(genders, 'MALE')}
                  label={{
                    htmlFor: 'genders_male',
                    children: (<FormattedMessage {...genderOptions.MALE} />),
                  }}
                />
              </span>
              <span styleName="checkbox">
                <Checkbox
                  data-cy="create-filter-form-gender-unspecified-checkbox"
                  id="genders_unspecified"
                  name="genders"
                  onChange={this.onChange}
                  value="UNSPECIFIED"
                  checked={includes(genders, 'UNSPECIFIED')}
                  label={{
                    htmlFor: 'genders_unspecified',
                    children: (<FormattedMessage {...genderOptions.UNSPECIFIED} />),
                  }}
                />
              </span>
            </div>
          </div>
          <div styleName="row">
            <div id="Location" styleName="label required">
              <span>
                <FormattedMessage {...messages.location} />
              </span>
            </div>
            <div role="group" aria-labelledby="Location" styleName="fields border">
              <FormattedMessage {...messages.within} />
              <Dropdown
                data-cy="create-filter-form-distance-dropdown"
                name="distanceValue"
                id="distance-value"
                placeholder={intl.formatMessage(messages.displaymiles)}
                selection
                styleName="inline-field"
                value={distanceValue}
                options={distanceOptions.map((d) => ({ key: d, text: intl.formatMessage(messages.miles, { value: d }), value: d }))}
                onChange={this.onChange}
                error={validationError && !distanceValue}
              />
              <FormattedMessage {...messages.outsideOf} />
              <Form.Input type="text" inline styleName="inline-field" error={(validationError && !postalCode) || incorrectFiveDigitsPostalCode}>
                <input
                  ref={this.labelAlert}
                  data-cy="create-filter-form-zipcode"
                  aria-label={intl.formatMessage(messages.postalCode)}
                  placeholder={intl.formatMessage(messages.postalCode)}
                  onChange={this.onChange}
                  id="postalCode"
                  name="postalCode"
                  value={postalCode}
                  maxLength="5"
                  aria-invalid={!!missingLocation || !!missingPostalCode || !!missingLocationandPostalCode || !!badPostalCode}
                />
              </Form.Input>
              <div role="alert" aria-atomic="true" styleName="labels-container">
                {missingLocation || missingPostalCode || missingLocationandPostalCode || badPostalCode}
                {(() => {
                  if (incorrectFiveDigitsPostalCode && this.labelAlert.current != null) {
                    this.labelAlert.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    return (
                      <Label key={uniqueId('incorrect-five-digits-post-')} basic color="red" pointing="above" styleName="postal-code-error">
                        <FormattedMessage {...messages.incorrectPostalCode} />
                      </Label>
                    );
                  } else {
                    return null;
                  }
                })()}
                {(() => {
                  if (serviceUnavailable && this.labelAlert.current != null) {
                    this.labelAlert.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    return (
                      <Label key={uniqueId('service-not-available-')} basic color="red" pointing="above" styleName="postal-code-error">
                        <FormattedMessage {...messages.serviceUnavailable} />
                      </Label>
                    );
                  } else {
                    return null;
                  }
                })()}
              </div>
            </div>
          </div>
          <div styleName="row">
            <div styleName="label" id="label-ethnicities"><FormattedMessage {...messages.ethnicity} /></div>
            <div styleName="dropdowns-container border" className="ethnicities-dropdown">
              {/* eslint-disable-next-line */}
              <div
                ref={this.ethnicityDropdownRef}
                styleName="dropdowns"
                onKeyDown={(event) => this.onDropdownKeydown(keyboardKey.getCode(event), 'ethnicities')}
              >
                <Dropdown
                  data-cy="create-filter-form-ethnicities-dropdown"
                  aria-labelledby="label-ethnicities"
                  name="ethnicities"
                  id="ethnicities"
                  selection
                  multiple
                  fluid
                  upward={false}
                  value={ethnicities}
                  options={Object.keys(ethnicityOptions).map((e) => ({ key: e, text: intl.formatMessage(ethnicityOptions[e]), value: e }))}
                  onChange={this.onChange}
                  onClick={() => this.ethnicityDropdownRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                />
              </div>
            </div>
          </div>
          <div styleName="row">
            <div styleName="label" id="label-grade-levels"><FormattedMessage {...messages.gradeLevel} /></div>
            <div styleName="dropdowns-container border">
              {/* eslint-disable-next-line */}
              <div
                ref={this.gradeLevelDropdownRef}
                styleName="dropdowns"
                onKeyDown={(event) => this.onDropdownKeydown(keyboardKey.getCode(event), 'gradeLevels')}
              >
                <Dropdown
                  data-cy="create-filter-form-grade-levels-dropdown"
                  aria-labelledby="label-grade-levels"
                  name="gradeLevels"
                  id="grade-levels"
                  selection
                  multiple
                  fluid
                  upward={false}
                  value={gradeLevels}
                  options={Object.keys(gradeLevelOptions).map((e) => (
                    { key: e, text: intl.formatMessage(gradeLevelOptions[e]), value: e }
                  ))}
                  onChange={this.onChange}
                  onClick={() => this.gradeLevelDropdownRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                />
              </div>
            </div>
          </div>
          <div styleName="row">
            <div styleName="label" id="label-letter-grades"><FormattedMessage {...messages.letterGrade} /></div>
            <div styleName="dropdowns-container border">
              {/* eslint-disable-next-line */}
              <div
                ref={this.gpaScoreDropdownRef}
                styleName="dropdowns"
                data-cy="create-filter-form-letter-grades-keydown"
                onKeyDown={(event) => this.onDropdownKeydown(keyboardKey.getCode(event), 'gpaScores')}
              >
                <Dropdown
                  data-cy="create-filter-form-letter-grades-dropdown"
                  aria-labelledby="label-letter-grades"
                  name="gpaScores"
                  id="letter-grades"
                  selection
                  multiple
                  fluid
                  upward={false}
                  value={gpaScores}
                  options={Object.keys(letterGradeOptions).map((e) => (
                    { key: e, text: intl.formatMessage(letterGradeOptions[e]), value: e }
                  ))}
                  onChange={this.onChange}
                  onClick={() => this.gpaScoreDropdownRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                />
              </div>
            </div>
          </div>
          <div styleName="row border">
            <div styleName="label"><FormattedMessage {...messages.preview} /></div>
            <div styleName="fields-center">
              {(postalCode && postalCode.length === 5 && distanceValue) ?
                <CollegeEventFilterMatches hideSummary params={params} /> :
                <span styleName="preview-msg"><FormattedMessage {...messages.previewMsg} /></span>}
            </div>
          </div>
          <div styleName="row">
            <label styleName="label required" htmlFor="name">
              <span>
                <FormattedMessage {...messages.name} />
              </span>
            </label>
            <div styleName="name">
              <Form.Input type="text" fluid error={(validationError && !name.trim()) || nameAlreadyExist}>
                <input
                  data-cy="create-filter-form-name-input"
                  aria-label={intl.formatMessage(messages.name)}
                  placeholder={intl.formatMessage(messages.name)}
                  onInput={this.handleChange}
                  onChange={this.handleFilterErrors}
                  id="name"
                  name="name"
                  value={name}
                  pattern="[a-zA-Z0-9 ]*"
                  maxLength="256"
                  aria-invalid={!!missingFilterName}
                />
              </Form.Input>
            </div>
            <div role="alert" aria-atomic="true">
              {missingFilterName}
              {nameAlreadyExist ? (
                <Label key={uniqueId('filter-already-exists-')} basic color="red" pointing="left">
                  <FormattedMessage {...messages.errorUniqueName} />
                </Label>
              ) : null}
            </div>
          </div>
          {this.renderButtons()}
          {this.renderCancelMessage()}
        </Form>
      </Dimmer.Dimmable>
    );
  }
}

export default injectIntl(CollegeEventsFilterFormComponent);
